import { loadResource } from 'iguazu-rest';
import {
  getActiveLocaleSelector,
  getHasAuthLoadedSelector,
  getIsLoggedInSelector,
} from '@americanexpress/travel-selectors';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { oneErrorOnNotOkFetchye, replaceDomainForCacheKey } from '@americanexpress/fetchye-amex';
import {
  setCardOffersLoaded,
  setCardOffersError,
  setCardOffersLoading,
} from '../ducks/homeDuck';
import {
  generateCardOffersOneCmsPath,
  getEligibleCardOffersOneCmsPath,
  getHasCardOffersErrors,
  getIsCardOffersLoading,
} from '../selectors/getHomeSelectors';
import {
  getCardBasedEligibleMarketingFiles,
  isCardTypeEnabled,
  validateEligibleCardOffersPath,
} from '../utils/personaUtils';
import { MODULE_NAME } from '../constants/modules';
import { getOneCMSDomainSelector } from '../selectors/getDomainSelectors';

export const loadHomepageCardOffersThunk = async (dispatch, getState) => {
  try {
    const state = getState();
    const activeLocale = getActiveLocaleSelector(state);
    const isLoggedIn = getIsLoggedInSelector(state);
    let eligiblePersonaStack = ['guest'];
    const oneCMSdomain = getOneCMSDomainSelector(state);
    dispatch(setCardOffersLoading());
    try {
      if (isLoggedIn) {
        eligiblePersonaStack = ['default'];
        const personaMapUrl = `${oneCMSdomain}/${generateCardOffersOneCmsPath(activeLocale, 'personamap')}`;
        const localePersonaMapFetchye = await dispatch(oneErrorOnNotOkFetchye(
          personaMapUrl,
          {
            mapKeyToCacheKey: replaceDomainForCacheKey('oneCMS/cardOffers/personamap'),
          },
          {
            moduleID: MODULE_NAME,
            component: 'loadHomepageCardOffersThunk',
            fetch: {
              url: personaMapUrl,
            },
          }
        ));
        const localePersonaMap = localePersonaMapFetchye.data.body;

        const travelerData = await dispatch(loadResource(
          {
            resource: 'traveler',
            id: activeLocale,
          }
        ));
        const cardBasedEligibleMarketingFiles = getCardBasedEligibleMarketingFiles(
          localePersonaMap,
          travelerData
        );
        eligiblePersonaStack = [...eligiblePersonaStack, ...cardBasedEligibleMarketingFiles];
      }
    } catch (error) {
      dispatch(addErrorToReport(
        error,
        {
          collectionMethod: 'loadHomepageCardOffersThunk',
          note: 'personaMap/Traveler fails to load',
          stackTrace: error,
          moduleID: MODULE_NAME,
        }
      ));
    } finally {
      let marketingData;
      while (eligiblePersonaStack.length > 0) {
        const nextPersona = eligiblePersonaStack.pop();
        const cardOffersEndpoint = generateCardOffersOneCmsPath(activeLocale, nextPersona);
        const oneCmsCardOffersurl = `${oneCMSdomain}/${cardOffersEndpoint}`;
        try {
          // eslint-disable-next-line no-await-in-loop -- we know this loop will definitely terminate
          const loadedData = await dispatch(oneErrorOnNotOkFetchye(
            oneCmsCardOffersurl,
            {
              mapKeyToCacheKey: replaceDomainForCacheKey(`oneCMS/cardOffers/${nextPersona}`),
            },
            {
              moduleID: MODULE_NAME,
              component: 'loadHomepageCardOffersThunk',
              fetch: {
                url: oneCmsCardOffersurl,
              },
            }
          ));
          const isLoadedDataEnabled = isCardTypeEnabled(loadedData.data.body);
          if (isLoadedDataEnabled) {
            marketingData = isLoadedDataEnabled;
            dispatch(setCardOffersLoaded(nextPersona));
            break;
          }
        } catch (error) {
          dispatch(addErrorToReport(
            error,
            {
              collectionMethod: 'loadHomepageCardOffersThunk',
              note: 'Card Offers one-cms path fails to load',
              moduleID: MODULE_NAME,
            }
          ));
        }
      }
      if (!marketingData) {
        dispatch(setCardOffersError());
        dispatch(addErrorToReport(
          new Error('Card Offers one-cms path do not exists or disabled'),
          {
            collectionMethod: 'loadHomepageCardOffersThunk',
            note: 'Card Offers one-cms path fails to load',
            moduleID: MODULE_NAME,
          }
        ));
      }
    }
  } catch (error) {
    dispatch(setCardOffersError());
    dispatch(addErrorToReport(
      error,
      {
        collectionMethod: 'loadHomepageCardOffersThunk',
        note: 'Unknown runtime error',
        moduleID: MODULE_NAME,
        component: 'src/thunks/loadHomepageCardOffersThunk',
      }
    ));
  }
};

export const queryHomepageCardOffers = (dispatch, getState) => {
  const state = getState();
  const eligibleCardOffersOneCmsPath = getEligibleCardOffersOneCmsPath(state);
  const isLoading = getIsCardOffersLoading(state);
  const isLoadError = getHasCardOffersErrors(state);
  const isLoggedIn = getIsLoggedInSelector(state);
  const hasAuthLoaded = getHasAuthLoadedSelector(state);

  if (isLoading) {
    return {
      promise: Promise.resolve(),
      error: false,
      status: 'loading',
    };
  }

  if (isLoadError) {
    return {
      promise: Promise.resolve(),
      error: isLoadError,
      status: 'complete',
    };
  }

  if (validateEligibleCardOffersPath(isLoggedIn, hasAuthLoaded, eligibleCardOffersOneCmsPath)) {
    return {
      promise: Promise.resolve(),
      error: false,
      status: 'complete',
    };
  }

  return {
    promise: dispatch(loadHomepageCardOffersThunk),
    error: false,
    status: 'loading',
  };
};

export default queryHomepageCardOffers;
